import { computed, Service, signal, type Signal } from '@angular/core';

/** A place in the manual: a chapter and, optionally, the section of it to bring into view. */
export interface ManualLocation {
  chapterId: string;
  sectionId: string | null;
}

/**
 * State of the contextual help panel; `ManualHelpPanel` (mounted once in the admin layout) renders
 * it. The site-header help action opens it on the chapter of the screen, or with no chapter for
 * searching. Inside the open panel, `navigate` walks a history of locations that `back` and
 * `forward` move through; the history lives only while the panel is open.
 */
@Service()
export class ManualHelpService {
  private readonly _isOpen = signal(false);
  private readonly _history = signal<ManualLocation[]>([]);
  private readonly _index = signal(-1);

  /** Whether the panel is open, with or without a chapter. */
  readonly isOpen: Signal<boolean> = this._isOpen.asReadonly();

  /** Current location; `null` when the panel is closed or opened for a search only. */
  readonly location: Signal<ManualLocation | null> = computed(
    () => this._history()[this._index()] ?? null,
  );

  /** Chapter shown by the panel; `null` when it is closed or opened for a search only. */
  readonly chapterId: Signal<string | null> = computed(() => this.location()?.chapterId ?? null);

  readonly canGoBack: Signal<boolean> = computed(() => this._index() > 0);
  readonly canGoForward: Signal<boolean> = computed(
    () => this._index() < this._history().length - 1,
  );

  /** Opens the side panel on that location with a new history, without leaving the screen. */
  openHelp(chapterId: string, sectionId?: string | null): void {
    this._history.set([{ chapterId, sectionId: sectionId ?? null }]);
    this._index.set(0);
    this._isOpen.set(true);
  }

  /** Opens the side panel with no location and an empty history, ready to search. */
  openSearch(): void {
    this._history.set([]);
    this._index.set(-1);
    this._isOpen.set(true);
  }

  /** Pushes a location inside the open panel, discarding the entries ahead of the current one. */
  navigate(chapterId: string, sectionId?: string | null): void {
    this._history.update((history) => [
      ...history.slice(0, this._index() + 1),
      { chapterId, sectionId: sectionId ?? null },
    ]);
    this._index.update((index) => index + 1);
  }

  back(): void {
    if (this.canGoBack()) this._index.update((index) => index - 1);
  }

  forward(): void {
    if (this.canGoForward()) this._index.update((index) => index + 1);
  }

  close(): void {
    this._isOpen.set(false);
    this._history.set([]);
    this._index.set(-1);
  }
}
